import { useNavigate } from 'react-router-dom';
import { LiksBrand } from '../LiksBrand/LiksBrand';
import { LiksButton } from '../LiksButton/LiksButton';
import { DEFAULT_LOCALE, type Locale } from '../../../i18n/resources';
import { translateForLocale } from '../../../i18n/translate';
import './LiksErrorFallback.css';

export type LiksErrorFallbackVariant = 'full' | 'inset';

export type LiksErrorFallbackProps = {
  variant?: LiksErrorFallbackVariant;
  showGoHome?: boolean;
  onRetry: () => void;
  onReload: () => void;
};

function resolveDocumentLocale(): Locale {
  const lang = document.documentElement.getAttribute('lang') ?? DEFAULT_LOCALE;
  return lang.toLowerCase().startsWith('de') ? 'de' : DEFAULT_LOCALE;
}

export function LiksErrorFallback({
  variant = 'full',
  showGoHome = false,
  onRetry,
  onReload,
}: LiksErrorFallbackProps) {
  const navigate = useNavigate();
  const locale = resolveDocumentLocale();
  const translate = (key: string) => translateForLocale(locale, key);

  const handleGoHome = () => {
    onRetry();
    navigate('/knowledge', { replace: true });
  };

  return (
    <div
      className={[
        'liks-error-fallback',
        `liks-error-fallback--${variant}`,
      ].join(' ')}
      role="alert"
    >
      <div className="liks-error-fallback__content">
        <LiksBrand className="liks-error-fallback__brand" />
        <h1 className="liks-error-fallback__title">
          {translate('errors.unexpected.title')}
        </h1>
        <p className="liks-error-fallback__description">
          {translate('errors.unexpected.description')}
        </p>
        <div className="liks-error-fallback__actions">
          {variant === 'full' ? (
            <>
              <LiksButton variant="primary" onClick={onReload}>
                {translate('errors.unexpected.reload')}
              </LiksButton>
              <LiksButton variant="secondary" onClick={onRetry}>
                {translate('errors.unexpected.retry')}
              </LiksButton>
            </>
          ) : (
            <>
              <LiksButton variant="primary" onClick={onRetry}>
                {translate('errors.unexpected.retry')}
              </LiksButton>
              {showGoHome && (
                <LiksButton variant="secondary" onClick={handleGoHome}>
                  {translate('errors.unexpected.goHome')}
                </LiksButton>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
